import { useState } from 'react';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveTranslation } from '@/utils/resolveTranslation';
import { translateRespondentMedia } from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';

interface MediaItem {
  fileId: string;
  url: string;
  type: string;
  transcriptionText?: string;
}

interface ConversationMedia {
  conversationId: string;
  respondentIndex: number;
  media: MediaItem[];
}

interface RespondentMediaGalleryProps {
  data: ConversationMedia[];
  isLoading: boolean;
  analysisLanguage: string;
}

function RespondentMediaGallery({
  data,
  isLoading,
  analysisLanguage,
}: RespondentMediaGalleryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  const totalMedia = data.reduce((sum, c) => sum + c.media.length, 0);

  if (!isLoading && totalMedia === 0) return null;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-32 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      </div>
    );
  }

  // Flatten all media items for the collapsed preview grid
  const allMedia = data.flatMap((c) =>
    c.media.map((m) => ({
      ...m,
      respondentIndex: c.respondentIndex,
      conversationId: c.conversationId,
    })),
  );

  const previewItems = allMedia.slice(0, 6);
  const remainingCount = allMedia.length - 6;

  return (
    <div className="flex flex-col w-full">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 mb-4"
      >
        <h3 className="text-sm font-semibold text-neutral-900">
          {resolveTranslation(translateRespondentMedia, analysisLanguage)}
        </h3>
        <span className="text-sm text-gray-500">
          ({totalMedia} {totalMedia === 1 ? 'item' : 'items'} from {data.length}{' '}
          {data.length === 1 ? 'respondent' : 'respondents'})
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {/* Preview grid (collapsed state) */}
      {!isExpanded && (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {previewItems.map((item, idx) => (
            <MediaThumbnail key={`${item.fileId}-${idx}`} item={item} />
          ))}
          {remainingCount > 0 && (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="flex items-center justify-center h-32 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
            >
              +{remainingCount} more
            </button>
          )}
        </div>
      )}

      {/* Full expanded view grouped by respondent */}
      {isExpanded && (
        <div className="flex flex-col gap-6">
          {data.map((conversation) => (
            <div key={conversation.conversationId} className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => openTranscriptPanel(conversation.conversationId)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
              >
                <Users className="h-4 w-4" />
                <span className="font-medium hover:underline">Respondent #{conversation.respondentIndex}</span>
                <span className="text-gray-400">
                  ({conversation.media.length} {conversation.media.length === 1 ? 'item' : 'items'})
                </span>
              </button>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {conversation.media.map((item, idx) => (
                  <MediaThumbnail
                    key={`${item.fileId}-${idx}`}
                    item={{
                      ...item,
                      respondentIndex: conversation.respondentIndex,
                      conversationId: conversation.conversationId,
                    }}
                    showTranscription
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface MediaThumbnailProps {
  item: {
    fileId: string;
    url: string;
    type: string;
    transcriptionText?: string;
    respondentIndex: number;
    conversationId: string;
  };
  showTranscription?: boolean;
}

function MediaThumbnail({ item, showTranscription = false }: MediaThumbnailProps) {
  if (item.type === 'image') {
    return (
      <div className="flex flex-col gap-1">
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          <img
            src={item.url}
            alt={`Respondent #${item.respondentIndex}`}
            className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer"
          />
        </a>
        {showTranscription && item.transcriptionText && (
          <p className="text-xs text-gray-500 line-clamp-2">{item.transcriptionText}</p>
        )}
      </div>
    );
  }

  if (item.type === 'video') {
    return (
      <div className="flex flex-col gap-1">
        <video
          src={item.url}
          controls
          preload="metadata"
          className="w-full h-32 object-cover rounded-lg border border-gray-200"
        />
        {showTranscription && item.transcriptionText && (
          <p className="text-xs text-gray-500 line-clamp-2">{item.transcriptionText}</p>
        )}
      </div>
    );
  }

  return null;
}

export default RespondentMediaGallery;
